/**
 * PackagesPage — Gestión de Paquetes, Precios e Historial.
 * 
 * Permite a la Community Manager:
 * 1. Definir qué incluye cada paquete (Básico, Intermedio, Premium y personalizados).
 * 2. Asignar paquetes a los clientes.
 * 3. Modificar precios y mantener un registro histórico de aumentos y motivos.
 */

import React, { useEffect, useState } from 'react';
import {
  Package,
  Plus,
  Edit2,
  Trash2,
  TrendingUp,
  History,
  Users,
  CheckCircle2,
  Clock,
  Loader2,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import type { PackagePlan, PackagePriceHistory, Client } from '@/types';

export function PackagesPage() {
  const [packages, setPackages] = useState<PackagePlan[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal Crear / Editar Paquete
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState<PackagePlan | null>(null);
  const [pkgForm, setPkgForm] = useState({
    name: '',
    badge_color: '#6366f1',
    description: '',
    featuresText: '',
    price: 0,
    priceChangeReason: '',
    updateAssignedClientsFees: false,
  });
  const [savingPkg, setSavingPkg] = useState(false);

  // Modal Historial de Precios
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedPkgForHistory, setSelectedPkgForHistory] = useState<PackagePlan | null>(null);
  const [historyItems, setHistoryItems] = useState<PackagePriceHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Modal Asignar a Clientes
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedPkgForAssign, setSelectedPkgForAssign] = useState<PackagePlan | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [updateClientFee, setUpdateClientFee] = useState(true);
  const [savingAssign, setSavingAssign] = useState(false);

  const loadData = async () => {
    try {
      const [pkgsRes, clientsRes] = await Promise.all([
        api.getPackages(),
        api.getClients('active'),
      ]);
      setPackages(pkgsRes.packages as PackagePlan[]);
      setClients(clientsRes.clients as Client[]);
    } catch (err) {
      console.error('Error cargando paquetes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ─── Handlers Paquete ───
  const openNewPackageModal = () => {
    setEditingPkg(null);
    setPkgForm({
      name: '',
      badge_color: '#6366f1',
      description: '',
      featuresText: '8 posts mensuales para el feed\n4 stories semanales\nDiseño gráfico en Canva\nReporte mensual de métricas',
      price: 25000,
      priceChangeReason: 'Precio inicial',
      updateAssignedClientsFees: false,
    });
    setEditModalOpen(true);
  };

  const openEditPackageModal = (pkg: PackagePlan) => {
    setEditingPkg(pkg);
    setPkgForm({
      name: pkg.name,
      badge_color: pkg.badge_color || '#6366f1',
      description: pkg.description || '',
      featuresText: Array.isArray(pkg.features) ? pkg.features.join('\n') : '',
      price: pkg.price,
      priceChangeReason: '',
      updateAssignedClientsFees: false,
    });
    setEditModalOpen(true);
  };

  const handleSavePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pkgForm.name || pkgForm.price <= 0) return;

    setSavingPkg(true);
    const featuresList = pkgForm.featuresText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      if (editingPkg) {
        await api.updatePackage(editingPkg.id, {
          name: pkgForm.name,
          badge_color: pkgForm.badge_color,
          description: pkgForm.description,
          features: featuresList,
          price: pkgForm.price,
          reason: pkgForm.priceChangeReason,
          update_assigned_clients_fees: pkgForm.updateAssignedClientsFees,
        });
      } else {
        await api.createPackage({
          name: pkgForm.name,
          badge_color: pkgForm.badge_color,
          description: pkgForm.description,
          features: featuresList,
          price: pkgForm.price,
          reason: pkgForm.priceChangeReason,
        });
      }
      setEditModalOpen(false);
      loadData();
    } catch (err) {
      console.error('Error guardando paquete:', err);
      alert('Error guardando paquete');
    } finally {
      setSavingPkg(false);
    }
  };

  const handleDeletePackage = async (id: string) => {
    if (!confirm('¿Eliminar este paquete y su historial de precios?')) return;
    try {
      await api.deletePackage(id);
      loadData();
    } catch (err) {
      console.error('Error eliminando paquete:', err);
    }
  };

  // ─── Handlers Historial ───
  const openHistoryModal = async (pkg: PackagePlan) => {
    setSelectedPkgForHistory(pkg);
    setHistoryModalOpen(true);
    setLoadingHistory(true);
    try {
      const res = await api.getPackage(pkg.id);
      setHistoryItems(res.package.history || []);
    } catch (err) {
      console.error('Error cargando historial de precios:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ─── Handlers Asignar Clientes ───
  const openAssignModal = (pkg: PackagePlan) => {
    setSelectedPkgForAssign(pkg);
    // Pre-seleccionar clientes que actualmente tienen este plan
    const currentAssigned = clients
      .filter((c) => c.plan_type?.toLowerCase() === pkg.name.toLowerCase())
      .map((c) => c.id);
    setSelectedClientIds(currentAssigned);
    setUpdateClientFee(true);
    setAssignModalOpen(true);
  };

  const handleToggleClientSelection = (clientId: string) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  const handleSaveAssignment = async () => {
    if (!selectedPkgForAssign) return;
    setSavingAssign(true);
    try {
      await api.assignPackage(selectedPkgForAssign.id, selectedClientIds, updateClientFee);
      setAssignModalOpen(false);
      loadData();
    } catch (err) {
      console.error('Error asignando plan:', err);
      alert('Error asignando el plan a los clientes');
    } finally {
      setSavingAssign(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      {/* ─── Cabecera ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Package className="text-indigo-600" />
            Paquetes y Precios
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Define qué incluye cada plan, costos actualizados y el historial de aumentos de tarifas.
          </p>
        </div>

        <Button onClick={openNewPackageModal} size="sm" className="gap-1.5 shadow-sm text-xs">
          <Plus size={14} />
          Nuevo Paquete
        </Button>
      </div>

      {/* ─── Grid de Paquetes ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg) => {
          const isPriceUpdated = pkg.last_price_change && pkg.last_price_change.previous_price;
          const priceDiff = isPriceUpdated
            ? pkg.price - (pkg.last_price_change?.previous_price || 0)
            : 0;
          const percentageChange =
            isPriceUpdated && pkg.last_price_change?.previous_price
              ? ((priceDiff / pkg.last_price_change.previous_price) * 100).toFixed(0)
              : null;

          return (
            <Card
              key={pkg.id}
              className="border border-slate-200/90 shadow-xs hover:shadow-md transition-all flex flex-col overflow-hidden bg-white group"
            >
              {/* Barra superior de color */}
              <div className="h-2 w-full" style={{ backgroundColor: pkg.badge_color }} />

              <CardHeader className="pb-3 pt-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg font-bold text-slate-900">
                        {pkg.name}
                      </CardTitle>
                      <Badge
                        style={{
                          backgroundColor: `${pkg.badge_color}18`,
                          color: pkg.badge_color,
                          borderColor: `${pkg.badge_color}30`,
                        }}
                        className="text-[11px] font-semibold border"
                      >
                        Plan Activo
                      </Badge>
                    </div>

                    {pkg.description && (
                      <CardDescription className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {pkg.description}
                      </CardDescription>
                    )}
                  </div>

                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                    <button
                      onClick={() => openEditPackageModal(pkg)}
                      className="p-1.5 text-slate-400 hover:text-slate-800 rounded-lg hover:bg-slate-100"
                      title="Editar paquete"
                    >
                      <Edit2 size={13} />
                    </button>
                    {packages.length > 1 && (
                      <button
                        onClick={() => handleDeletePackage(pkg.id)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50"
                        title="Eliminar paquete"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Precio destacado */}
                <div className="pt-3 border-t border-slate-100 flex items-baseline justify-between">
                  <div>
                    <span className="text-2xl font-extrabold text-slate-900 tracking-tight">
                      {formatCurrency(pkg.price)}
                    </span>
                    <span className="text-xs text-muted-foreground ml-1">/ mes</span>
                  </div>

                  {percentageChange && (
                    <Badge variant="outline" className="text-[10px] text-emerald-700 bg-emerald-50 border-emerald-200">
                      <TrendingUp size={10} className="mr-0.5" />
                      +{percentageChange}%
                    </Badge>
                  )}
                </div>

                {/* Fecha de última actualización */}
                <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-1">
                  <Clock size={11} />
                  <span>
                    Actualizado: {pkg.updated_at ? formatDate(pkg.updated_at) : 'Reciente'}
                  </span>
                </div>
              </CardHeader>

              {/* Lo que incluye */}
              <CardContent className="flex-1 space-y-4 pt-1">
                <div>
                  <h5 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                    Lo que incluye:
                  </h5>
                  <ul className="space-y-1.5">
                    {Array.isArray(pkg.features) && pkg.features.length > 0 ? (
                      pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                          <CheckCircle2 size={14} className="text-emerald-500 shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-xs text-muted-foreground italic">Sin detalles especificados</li>
                    )}
                  </ul>
                </div>

                {/* Clientes Asignados */}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-semibold text-slate-700 flex items-center gap-1">
                      <Users size={12} className="text-slate-400" />
                      Clientes con este plan:
                    </span>
                    <span className="text-slate-500 font-medium">
                      {pkg.client_count || 0}
                    </span>
                  </div>

                  {pkg.clients && pkg.clients.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {pkg.clients.map((c) => (
                        <span
                          key={c.id}
                          className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                          style={{
                            backgroundColor: `${c.avatar_color || '#6366f1'}15`,
                            color: c.avatar_color || '#6366f1',
                          }}
                        >
                          {c.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground italic">
                      Ningún cliente asignado todavía.
                    </p>
                  )}
                </div>

                {/* Acciones de Tarjeta */}
                <div className="pt-2 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openHistoryModal(pkg)}
                    className="flex-1 text-xs gap-1 text-slate-700 hover:text-slate-900 hover:bg-slate-50"
                  >
                    <History size={12} />
                    Historial
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openAssignModal(pkg)}
                    className="flex-1 text-xs gap-1 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                  >
                    <Users size={12} />
                    Asignar
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ─── Modal: Crear / Editar Paquete y Tarifa ─── */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 text-sm">
                {editingPkg ? `Editar Paquete: ${editingPkg.name}` : 'Crear Nuevo Paquete'}
              </h3>
              <button onClick={() => setEditModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSavePackage} className="p-5 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Nombre del Paquete *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Básico, Intermedio, Premium, Plan Reels..."
                    value={pkgForm.name}
                    onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Color
                  </label>
                  <input
                    type="color"
                    value={pkgForm.badge_color}
                    onChange={(e) => setPkgForm({ ...pkgForm, badge_color: e.target.value })}
                    className="w-full h-8 px-1 py-0.5 border border-slate-200 rounded-lg cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Descripción Corta
                </label>
                <input
                  type="text"
                  placeholder="Enfoque del paquete y para quién está pensado..."
                  value={pkgForm.description}
                  onChange={(e) => setPkgForm({ ...pkgForm, description: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              {/* Precio y Motivo de Cambio */}
              <div className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/80 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-800 block mb-1">
                      Costo / Tarifa Mensual ($ ARS) *
                    </label>
                    <input
                      type="number"
                      required
                      min={0}
                      step={500}
                      value={pkgForm.price}
                      onChange={(e) =>
                        setPkgForm({ ...pkgForm, price: parseFloat(e.target.value) || 0 })
                      }
                      className="w-full px-3 py-2 text-xs border border-slate-300 bg-white rounded-lg font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    />
                  </div>

                  {editingPkg && (
                    <div className="flex flex-col justify-end">
                      <span className="text-[11px] text-slate-500">Tarifa actual:</span>
                      <span className="text-xs font-semibold text-slate-700">
                        {formatCurrency(editingPkg.price)} / mes
                      </span>
                    </div>
                  )}
                </div>

                {editingPkg && editingPkg.price !== pkgForm.price && (
                  <div>
                    <label className="text-xs font-semibold text-indigo-900 block mb-1">
                      Motivo del cambio de precio (para el historial):
                    </label>
                    <input
                      type="text"
                      placeholder="Ej: Ajuste por inflación octubre, se sumaron 2 reels al plan..."
                      value={pkgForm.priceChangeReason}
                      onChange={(e) => setPkgForm({ ...pkgForm, priceChangeReason: e.target.value })}
                      className="w-full px-3 py-1.5 text-xs border border-indigo-200 bg-indigo-50/50 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    />

                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pkgForm.updateAssignedClientsFees}
                        onChange={(e) =>
                          setPkgForm({ ...pkgForm, updateAssignedClientsFees: e.target.checked })
                        }
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-[11px] text-slate-600">
                        Actualizar la cuota mensual a los clientes que tienen este paquete activo
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Lo que incluye */}
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Lo que incluye (1 característica por línea)
                </label>
                <textarea
                  rows={5}
                  placeholder="8 posts mensuales para el feed&#10;4 stories semanales&#10;Diseño en Canva&#10;Reporte mensual de métricas"
                  value={pkgForm.featuresText}
                  onChange={(e) => setPkgForm({ ...pkgForm, featuresText: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Escribe cada servicio o entregable en un renglón separado. Se mostrarán con viñetas en la tarjeta.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditModalOpen(false)}
                  className="text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={savingPkg}
                  className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {savingPkg ? <Loader2 size={13} className="animate-spin" /> : 'Guardar Paquete'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal: Historial y Seguimiento de Precios ─── */}
      {historyModalOpen && selectedPkgForHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <History className="text-indigo-600 w-4 h-4" />
                <h3 className="font-semibold text-slate-900 text-sm">
                  Historial de Precios: {selectedPkgForHistory.name}
                </h3>
              </div>
              <button onClick={() => setHistoryModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X size={16} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200/60 flex items-center justify-between">
                <div>
                  <span className="text-[11px] text-slate-500">Precio Actual</span>
                  <p className="text-lg font-bold text-slate-900">
                    {formatCurrency(selectedPkgForHistory.price)} / mes
                  </p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {historyItems.length} cambios registrados
                </Badge>
              </div>

              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                </div>
              ) : historyItems.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">
                  No hay cambios de precio registrados aún.
                </p>
              ) : (
                <div className="relative pl-6 space-y-6 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                  {historyItems.map((item, idx) => {
                    const diff = item.previous_price ? item.price - item.previous_price : 0;
                    const pct =
                      item.previous_price && item.previous_price > 0
                        ? ((diff / item.previous_price) * 100).toFixed(0)
                        : null;

                    return (
                      <div key={item.id || idx} className="relative group">
                        {/* Punto en la línea de tiempo */}
                        <div className="absolute -left-6 top-1 w-2.5 h-2.5 rounded-full bg-indigo-600 ring-4 ring-white" />

                        <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-900">
                              {formatCurrency(item.price)}
                            </span>

                            {pct && (
                              <span className="text-[11px] font-semibold text-emerald-600 flex items-center">
                                +{pct}%
                              </span>
                            )}
                          </div>

                          {item.previous_price && (
                            <p className="text-[11px] text-slate-400 mt-0.5">
                              Anterior: {formatCurrency(item.previous_price)}
                            </p>
                          )}

                          {item.reason && (
                            <p className="text-xs text-slate-700 mt-1.5 font-medium">
                              📝 {item.reason}
                            </p>
                          )}

                          <span className="text-[10px] text-slate-400 block mt-2">
                            {formatDate(item.effective_date || item.created_at)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-slate-100 flex justify-end">
              <Button size="sm" variant="outline" onClick={() => setHistoryModalOpen(false)} className="text-xs">
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal: Asignar Paquete a Clientes ─── */}
      {assignModalOpen && selectedPkgForAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">
                  Asignar {selectedPkgForAssign.name}
                </h3>
                <p className="text-[11px] text-slate-500">
                  Tarifa: {formatCurrency(selectedPkgForAssign.price)}/mes
                </p>
              </div>
              <button onClick={() => setAssignModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              <p className="text-xs text-slate-600">
                Selecciona los clientes que contratarán o migrarán a este paquete:
              </p>

              <div className="space-y-2 max-h-60 overflow-y-auto border border-slate-200 rounded-xl p-2 bg-slate-50/50">
                {clients.map((client) => {
                  const isSelected = selectedClientIds.includes(client.id);
                  return (
                    <div
                      key={client.id}
                      onClick={() => handleToggleClientSelection(client.id)}
                      className={cn(
                        'flex items-center justify-between p-2.5 rounded-lg border text-xs cursor-pointer transition-colors',
                        isSelected
                          ? 'bg-indigo-50 border-indigo-200 text-indigo-900'
                          : 'bg-white border-slate-200 hover:bg-slate-100/70 text-slate-800'
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // controlado por div
                          className="rounded border-slate-300 text-indigo-600"
                        />
                        <div className="truncate">
                          <p className="font-semibold truncate">{client.name}</p>
                          <span className="text-[10px] text-slate-400">
                            Plan actual: {client.plan_type || 'Sin plan'}
                          </span>
                        </div>
                      </div>

                      <span className="text-[11px] font-mono text-slate-500 shrink-0">
                        {formatCurrency(client.monthly_fee)}/mes
                      </span>
                    </div>
                  );
                })}
              </div>

              <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-slate-100">
                <input
                  type="checkbox"
                  checked={updateClientFee}
                  onChange={(e) => setUpdateClientFee(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 mt-0.5"
                />
                <span className="text-xs text-slate-600">
                  Actualizar la cuota mensual de estos clientes a la tarifa de este paquete ({formatCurrency(selectedPkgForAssign.price)})
                </span>
              </label>
            </div>

            <div className="p-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500">
                {selectedClientIds.length} cliente(s) seleccionado(s)
              </span>

              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setAssignModalOpen(false)} className="text-xs">
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveAssignment}
                  disabled={savingAssign}
                  className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {savingAssign ? <Loader2 size={13} className="animate-spin" /> : 'Guardar Asignación'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
